'use strict';

/**
 * @ngdoc service
 * @name 360StoreAngularApp.storeUtils
 * @description
 * # storeUtils
 * Service in the 360StoreAngularApp.
 */
angular.module('360StoreAngularApp')
	.service('storeUtils', function () {

		/**
		 * @return {string} Current date in YYYY-MM-DD format
		 */
		this.currentDate = () => {
			return new Date().toLocaleDateString('sv-SE'); // YYYY-mm-dd format
		};

		/**
		 *
		 * @param date {string|null} starting date in YYYY-MM-DD, or Date object, or `null` for current date
		 * @param days {number} days to add
		 * @return {string} date + days in YYYY-MM-DD format
		 */
		this.addDate = (date, days) => {
			return window.moment(date || this.currentDate()).add(days, 'day').format('YYYY-MM-DD');
		};

		/**
		 * Parses a date string from a variety of common, non-standard formats.
		 *
		 * @param {string} dateString The date string to parse.
		 * @returns {Date} A JavaScript Date object.
		 * @throws {Error} If the date string does not match any of the supported formats.
		 */
		this.parseDateFlexible = (dateString) => {
			// An array of formats to try in order. Moment will find the first match.
			const formats = [
				// Handles both "2025-09-17T21:04:02Z" and "2025-09-17 21:29:32"
				// This is the most robust way, as it uses the library's native ISO 8601 support.
				moment.ISO_8601,
				// Format: "Jan 4, 2020 2:56:23 PM"
				'MMM D, YYYY h:mm:ss A',
				// Format: "6/26/24, 2:00 AM" or "9/3/25, 4:54 PM"
				'M/D/YY, h:mm A',
				// Format: "9/3/2025"
				'M/D/YYYY'
			];

			// The 'true' flag enables strict parsing for the provided formats.
			const parsedDate = moment(dateString, formats, true);

			if (!parsedDate.isValid()) {
				// Throw an error if none of the formats matched.
				throw new Error('Date string could not be parsed by any known format.');
			}
			return parsedDate.toDate();
		};

		/**
		 * @param object {Record} a record such as a da or contact
		 * @param keyParts {string[]} an array of keyPaths such as `['tripLegs[0]`, 'locationFirst', 'city']`
		 * @return the value of `keyParts` in `object`
		 */
		const getValueByKeyParts = (object, keyParts) => keyParts.reduce((obj, key) => obj && (key.includes('[') ? obj[key.split('[')[0]][key.split('[')[1].slice(0, -1)] : obj[key]), object);


		/**
		 * Useful for sorting, e.g. `arr.sort(trips, itinerisUtils.compareFn('serviceDate', 'pickupTimeOrdered', 'passenger.name desc))`
		 * @param keys the keys / keyPaths to use when comparing values. Supports dot notation, `desc` keyword.
		 * @return {function(*,*): 1|0|1} comparator function you can pass to `Array.sort()`
		 */
		this.compareFn = (...keys) => {
			const valueGetter = getValueByKeyParts;
			const keyDefinitions = keys.map(keyString => {
				let sortOrder = 1;
				if (keyString.toLowerCase().endsWith(' desc')) {
					sortOrder *= -1;
					keyString = keyString.slice(0, -5);
				} else if (keyString.toLowerCase().endsWith(' asc')) { // redundant, but we'll allow it
					keyString = keyString.slice(0, -4);
				}
				return {sortOrder, parts: keyString.split('.')};
			});
			return function (o1, o2) {
				for (const keyDef of keyDefinitions) {
					// const [v1, v2] = [o1, o2].map(obj => keyDef.parts.reduce((a, b) => a?.[b], obj));
					const [v1, v2] = [o1, o2].map(obj => valueGetter(obj, keyDef.parts));
					if (v1 < v2) {
						return -1 * keyDef.sortOrder;
					} else if (v1 > v2) {
						return keyDef.sortOrder;
					}
				}
				return 0;
			};
		};


		/**
		 * An efficient filter which relies on the caller converting `q` to lowercase. This is called very frequently in tight loops.
		 * @param s a string to search
		 * @param q a *lowercase* string to find in `s`.
		 * @return {boolean} whether lowercase `s` contains `q`
		 */
		this.containsIgnoreCase = function (s, q) {
			return !q || (s && s.toLowerCase().includes(q));
		};


		this.downloadStringContents = (content, filename, mimeType) => {
			let blob = new Blob([content], {type: mimeType});
			let link = document.createElement('a');
			// feature detection
			let url = URL.createObjectURL(blob);
			link.setAttribute('href', url);
			link.setAttribute('download', filename);
			link.style.visibility = 'hidden';
			window.document.body.appendChild(link);
			link.click();
			window.document.body.removeChild(link);
		};


		/**
		 * itinerisUtils.memoizeMetadata worker function
		 *
		 * @callback memoizeCallback
		 * @template T
		 * @param {Record} businessObject
		 * @return {T} memoized result
		 */

		/**
		 * Memoize a value and store it in the object's _metadata, adding a non-enumerable `_metadata` object to `businessObject` if necessary.
		 * @template T
		 * @param businessObject {Record} any object which can have _metadata
		 * @param key {string} key to look up in _metadata
		 * @param {memoizeCallback<T>} worker  worker to invoke if object _metadata[key] is undefined. The businessObject will be passed as a parameter
		 * @return {T} the memoized value
		 */
		this.memoizeMetadata = (businessObject, key, worker) => {
			if (!businessObject) {
				return null;
			}
			if (businessObject._metadata === undefined || businessObject.propertyIsEnumerable('_metadata')) {
				// make _metadata non-enumerable, feel free to throw anything you like in here
				Object.defineProperty(businessObject, '_metadata', {value: businessObject._metadata || {}, enumerable: false, configurable: true, writable: true});
			}
			let meta = businessObject._metadata;
			if (meta[key] === undefined) {
				meta[key] = worker(businessObject);
			}
			return meta[key];
		};

	});
